import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SignInHeader from "../auth/SigninHeader";
import Navigation from "../layout/Navigation";

const TriggerLocation = () => {
  const navigate = useNavigate();

  const [authenticated, setAuthenticated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [group, setGroup] = useState("");
  const [type, setType] = useState("");
  const [subtype, setSubType] = useState("");
  const [category, setCategory] = useState("");
  const [user, setUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    const savedTheme = localStorage.getItem("theme");
    const prefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    
    setIsDarkMode(savedTheme === "dark" || (!savedTheme && prefersDark));
    
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
        localStorage.removeItem('user');
      }
    }

    // Load stored data with defaults
    setSelectedTemplate(localStorage.getItem("selectedTemplate") || "AA - Received");
    setGroup(localStorage.getItem("group") || "Building");
    setType(localStorage.getItem("type") || "Residential");
    setSubType(localStorage.getItem("subtype") || "NA");
    setCategory(localStorage.getItem("category") || "NA");

    // Check authentication status (same as TemplateViewer and RecordTypeSelector)
    const isAuthenticated = !!token;
    setAuthenticated(isAuthenticated);

    if (!isAuthenticated) {
      setErrorMessage("Please sign in to access this page.");
      navigate("/signin");
      return;
    }

    console.log('TriggerLocation loaded with data:', {
      selectedTemplate,
      group,
      type,
      subtype,
      category,
      authenticated: isAuthenticated
    });
  }, [navigate]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
  };

  const handleTemplateClick = () => {
    if (selectedTemplate) {
      navigate("/template-viewer", { state: { selectedTemplate } });
    }
  };

  const handleRecordClick = () => {
    navigate("/record-types", {
      state: { group, type, subtype, category }
    });
  };

  const handleTaskSelection = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setErrorMessage("Please sign in to continue.");
        navigate("/signin");
        return;
      }

      const recordTypeData = {
        group,
        type,
        subtype,
        category,
        selectedTemplate
      };

      console.log('Sending record type data:', recordTypeData);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/set-record-type`, 
        recordTypeData, 
        {
          headers: { 
            Authorization: token,
            'Content-Type': 'application/json'
          },
        }
      );

      if (response.data.success) {
        console.log('Record type data saved successfully');
        navigate("/taskselection");
      } else {
        setErrorMessage(response.data.message || 'Failed to save record type data');
      }
    } catch (error) {
      console.error("Error sending record type data:", error);
      
      if (error.response?.status === 401) {
        setErrorMessage("Your session has expired. Please sign in again.");
        localStorage.removeItem('token');
        navigate("/signin");
      } else {
        setErrorMessage("Failed to save data. Please try again.");
        // Still navigate to taskselection on error (as per original behavior)
        navigate("/taskselection");
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state if not authenticated
  if (!authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-main-dark' : 'bg-gray-100'}`}>
        <div className="text-center p-6 rounded-xl bg-red-50 text-red-600 max-w-md">
          <div className="mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <p className="text-lg font-medium">{errorMessage}</p>
          <button
            onClick={() => navigate('/signin')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-main-dark text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <SignInHeader
        user={user}
        isLoggedIn={authenticated}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />
      <Navigation
        activeTab="My Apps"
        setActiveTab={() => {}}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      <div className="flex flex-col items-center pt-[140px] pb-10 px-5 flex-grow">
        <div className="self-start ml-6 mt-4 mb-3 px-3 py-2">
          <button
            onClick={handleTemplateClick}
            className="bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800"
            disabled={!selectedTemplate}
          >
            {selectedTemplate || "No Template Selected"} |
          </button>
          <button
            onClick={handleRecordClick}
            className="ml-1 bg-transparent border-none text-purple-700 font-medium cursor-pointer text-sm hover:text-purple-800"
            disabled={!group || !type || !subtype || !category}
          >
            {`${group}/${type}/${subtype}/${category}`}
          </button>
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-2xl text-[#333] font-bold">
            How do you want to trigger the communication?
          </h1>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="w-full max-w-md mb-4 p-3 rounded bg-red-50 border border-red-200">
            <div className="flex items-center justify-between">
              <p className="text-red-600 text-sm">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage("")}
                className="text-red-400 hover:text-red-600 text-lg"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 grid-rows-2 gap-px w-full max-w-[500px] max-h-[200px]">
          <button
            className="flex items-center justify-center p-6 bg-[#41425A] text-white hover:bg-[#100E27] transition duration-300"
            disabled={loading}
          >
            <div className="text-center">
              <h3 className="text-xl font-medium">Workflow Task Status Update</h3>
            </div>
          </button>

          <button 
            className="flex items-center justify-center p-5 bg-[#41425A] text-white hover:bg-[#100E27] transition duration-300"
            disabled={loading}
          >
            <div className="text-center">
              <h3 className="text-xl font-medium">Record Status Update</h3>
            </div>
          </button>

          <button
            onClick={handleTaskSelection}
            className={`flex items-center justify-center p-5 bg-[#41425A] text-white hover:bg-[#100E27] transition duration-300 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={loading}
          >
            <div className="text-center">
              <h3 className="text-xl font-medium">
                {loading ? 'Loading...' : 'Inspection Status Update'}
              </h3>
            </div>
          </button>

          <button 
            className="flex items-center justify-center p-5 bg-[#41425A] text-white hover:bg-[#100E27] transition duration-300"
            disabled={loading}
          >
            <div className="text-center">
              <h3 className="text-xl font-medium">Batch Reminders</h3>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TriggerLocation;